import { ethers, getDefaultProvider } from 'ethers'
import { LEDGER_EMBEDDED_QUERY_PARAM } from 'lib/constants'
import * as yup from 'yup'
import { isAPNG, toBuffer } from './utils'

function isValidAddress(str: string): boolean {
  try {
    ethers.utils.getAddress(str)
    return true
  } catch (error) {
    return false
  }
}

async function isValidAddressOrENS(str: string): Promise<boolean> {
  if (isValidAddress(str)) {
    return true
  } else {
    try {
      let response = await resolveName(str)
      if (response) return true
    } catch (error) {
      return false
    }
  }
  return false
}

async function resolveName(name: string): Promise<string | null> {
  const mainnetProvider = getDefaultProvider('homestead')
  return await mainnetProvider.resolveName(name)
}

const isValidEmail = (email: string) => {
  return yup.string().email().isValidSync(email)
}

const convertToGWEI = (numberInWEI: string) => {
  return Number(ethers.utils.formatUnits(numberInWEI, 'gwei')).toString()
}

const convertToETH = (numberInWEI: string) => {
  return Number(ethers.utils.formatUnits(numberInWEI, 'ether'))
}

const convertFromGWEI = (numberInGWEI: string) => {
  let numberGWEI: number = Number(numberInGWEI)
  for (let i = 1; i < 10; i++) {
    numberGWEI = Number(numberGWEI) * 10
  }
  return String(numberGWEI)
}

const reduceAddress = (address: string) => {
  if (address.length < 10) return address
  return address.slice(0, 6) + '\u2026' + address.slice(-4)
}

const generateSecretCode = () =>
  Math.floor(Math.random() * 999999)
    .toString()
    .padStart(6, '0')

const getBase64 = (img: File | Blob, callback: (outputFile: string | undefined) => void) => {
  const reader = new FileReader()

  reader.addEventListener('load', () => {
    if (typeof reader.result === 'string') return reader.result
  })
  reader.readAsDataURL(img)
}

function getNumberWithOrdinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'],
    v = n % 100
  return n + (s[(v - 20) % 10] || s[v] || s[0])
}

// #region Validate PNG files

class MimeTypes {
  static readonly PNG_SIGNATURE = '89504E47'
  static readonly PNG_FYLE_TYPE = 'image/png'
  static readonly GIF_SIGNATURE = '47494638'
  static readonly GIF_FYLE_TYPE = 'image/gif'
  static readonly JPEG_SIGNATURE_1 = 'FFD8FFDB'
  static readonly JPEG_SIGNATURE_2 = 'FFD8FFE0'
  static readonly JPEG_SIGNATURE_3 = 'FFD8FFEE'
  static readonly JPEG_SIGNATURE_4 = 'FFD8FFE1'
  static readonly JPEG_FYLE_TYPE = 'image/jpg'
  static readonly WEBP_SIGNATURE = '52494646'
  static readonly WEBP_FYLE_TYPE = 'image/webp'
  static readonly UNKNOWN_FYLE_TYPE = 'Unknown filetype'
}

const getMimetype = (signature: string) => {
  console.log(signature)
  switch (signature) {
    case MimeTypes.PNG_SIGNATURE:
      return MimeTypes.PNG_FYLE_TYPE
    case MimeTypes.GIF_SIGNATURE:
      return MimeTypes.GIF_FYLE_TYPE
    case MimeTypes.JPEG_SIGNATURE_1:
    case MimeTypes.JPEG_SIGNATURE_2:
    case MimeTypes.JPEG_SIGNATURE_3:
    case MimeTypes.JPEG_SIGNATURE_4:
      return MimeTypes.JPEG_FYLE_TYPE
    case MimeTypes.WEBP_SIGNATURE:
      return MimeTypes.WEBP_FYLE_TYPE
    default:
      return MimeTypes.UNKNOWN_FYLE_TYPE
  }
}

const isValidFormat = (arrBuffer: ArrayBuffer, file: File) => {
  const fileHeaders = arrBuffer.slice(0, 4) // Take first 4 bytes of the file as headers
  const uInt = new Uint8Array(fileHeaders) // A byte is 8-bit so when can use Uint8Array to hold the values
  let bytes: string[] = [] // Get hexadecimal code as a string
  uInt.forEach((byte) => {
    // Retrieve every byte and transform it to hexadecimal
    bytes.push(byte.toString(16))
  })
  const hex = bytes.join('').toUpperCase()

  const fileType = file.type ? file.type : 'Unknown/Extension missing'
  const binaryFileType = getMimetype(hex)

  // Allowed Files are just non-APNG PNG's and GIF's and WebP's
  const binaryFileTypeIsGIF = binaryFileType === MimeTypes.GIF_FYLE_TYPE
  const binaryFileTypeIsPNG = binaryFileType === MimeTypes.PNG_FYLE_TYPE
  const binaryFileTypeIsWebP = binaryFileType === MimeTypes.WEBP_FYLE_TYPE
  const fileTypeisAPNG = isAPNG(toBuffer(arrBuffer))

  // Allowed File Extension is just PNG
  const fileExtension =
    fileType === MimeTypes.PNG_FYLE_TYPE ||
    fileType === MimeTypes.GIF_FYLE_TYPE ||
    fileType === MimeTypes.WEBP_FYLE_TYPE

  const isValid =
    fileExtension && (binaryFileTypeIsGIF || binaryFileTypeIsPNG || binaryFileTypeIsWebP) && fileTypeisAPNG === false

  return isValid
}

const readFile = (file: File): Promise<boolean> => {
  const fileReader = new FileReader()

  return new Promise((resolve, reject) => {
    // File load successful
    fileReader.onloadend = (fileReaderEvent: any) => {
      if (fileReaderEvent.target.readyState === FileReader.DONE) {
        const result = isValidFormat(fileReaderEvent.target.result, file)
        resolve(result)
      }
    }

    // File load unsuccessful
    fileReader.onerror = () => {
      fileReader.abort()
      reject(false)
    }

    //Read the 4 bytes previously sliced
    fileReader.readAsArrayBuffer(file)
  })
}

const isValidImage = async (file: File) => {
  try {
    const res = await readFile(file)
    return res
  } catch (e) {
    return false
  }
}

const isValidSize = (file: File) => {
  const ONE_MB = 1024
  const MAXIMUM_IMAGE_SIZE_MB = 4096

  const size = file.size
  const fileSize = Math.round(size / ONE_MB)

  const fileSizeTooBig = fileSize > MAXIMUM_IMAGE_SIZE_MB

  return !fileSizeTooBig
}

// #endregion

// #region Handle error messages

class ErrorCodes {
  static readonly INVALID_CREDENTIALS = 'INVALID_CREDENTIALS'
  static readonly FORBIDDEN = 'FORBIDDEN'
  static readonly EDITION_EXPIRED = 'EDITION_EXPIRED'
  static readonly EVENT_EDIT_BAD_REQUEST = 'EVENT_EDIT_BAD_REQUEST'
}

class ErrorMessages {
  static readonly INVALID_SECRET_CODE = 'Invalid code. Please check the edit code and try again'
  static readonly NOT_AUTHORIZED = 'Not Authorized'
  static readonly DEFAULT_ERROR = 'An error has ocurred'
}

const getErrorMessageFromCode = (errorCode: string, authenticated: boolean): string => {
  switch (true) {
    case errorCode === ErrorCodes.INVALID_CREDENTIALS:
    case errorCode === ErrorCodes.EVENT_EDIT_BAD_REQUEST:
    case errorCode === ErrorCodes.FORBIDDEN && authenticated:
      return ErrorMessages.NOT_AUTHORIZED
    case errorCode === ErrorCodes.FORBIDDEN && !authenticated:
      return ErrorMessages.INVALID_SECRET_CODE
    default:
      // For retro-compatibility, in case of default we should return the error we get from the API
      return errorCode
  }
}

export const isEmbed = (): boolean => {
  const params = new URLSearchParams(window.self.location.search)
  return !!params.get(LEDGER_EMBEDDED_QUERY_PARAM)
}

export const isIframe = (): boolean => {
  try {
    return window.self !== window.top
  } catch (error) {
    return false
  }
}

export const isLedgerDappBrowserProvider = (() => {
  let state: boolean | null = null

  return (): boolean => {
    if (typeof state === 'boolean') return state
    if (typeof window === 'undefined') return false

    try {
      state = isIframe() && isEmbed()
    } catch (error) {
      state = false
    } finally {
      // eslint-disable-next-line no-unsafe-finally
      return !!state
    }
  }
})()

// #endregion

export {
  getBase64,
  isValidAddress,
  isValidAddressOrENS,
  convertToGWEI,
  convertFromGWEI,
  convertToETH,
  reduceAddress,
  generateSecretCode,
  isValidEmail,
  getNumberWithOrdinal,
  isValidImage,
  isValidSize,
  getErrorMessageFromCode,
}
